import { compose, lifecycle, withStateHandlers } from 'recompose';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { addIntlProps, addProps } from 'utils/recompose';
import formatCurrencyGeneral from 'utils/formatCurrencyGeneral';
import messages from './messages';
import combineSelectors from 'utils/combineSelectors';
import { theme } from 'reducers/dashboard/selectors';
import {
  selectedTemplatesByPriceBand,
  suggestedTemplatesByPriceBand,
  priceBands,
  currentSelectedQuantity,
  suggestedTemplateQuantity,
} from 'reducers/models/selectors';
import styles from './styles.module.scss';
import { isEqual } from 'lodash';
import { isInitialState } from 'utils/isInitialState';
import { setObjValuesToFalse } from 'utils/setObjValuesToFalse';

const mapStateToProps = combineSelectors({
  selectedTemplatesByPriceBand,
  suggestedTemplatesByPriceBand,
  priceBands,
  currentSelectedQuantity,
  suggestedTemplateQuantity,
  theme,
});

const initialStateKeys = {
  showSelected: 'showSelected',
  showSuggested: 'showSuggested',
};

export function componentDidMount() {
  this.props.formatChartData();
}

export function componentDidUpdate(prevProps) {
  if (
    !isEqual(
      prevProps.selectedTemplatesByPriceBand,
      this.props.selectedTemplatesByPriceBand,
    ) ||
    (prevProps && !isEqual(prevProps.modelId, this.props.modelId))
  ) {
    this.props.formatChartData();
  }
}

export const initialState = { showSelected: true, showSuggested: true };

export const toggleSelectedLine = props => () => {
  const { showSelected } = props;
  if (isInitialState(props)) {
    return setObjValuesToFalse(props, initialStateKeys.showSelected);
  }
  return { showSelected: !showSelected };
};

export const toggleSuggestedArea = props => () => {
  const { showSuggested } = props;
  if (isInitialState(props)) {
    return setObjValuesToFalse(props, initialStateKeys.showSuggested);
  }
  return { showSuggested: !showSuggested };
};

export const resetFilters = ({ showSuggested, showSelected }) => () =>
  initialState;

export const selectedFilterStyle = ({ showSelected }) =>
  showSelected ? styles.inactiveFilter : styles.activeFilter;

export const suggestedFilterStyle = ({ showSuggested }) =>
  showSuggested ? styles.inactiveFilter : styles.activeFilter;

export const formatChartData = (
  state,
  {
    selectedTemplatesByPriceBand,
    suggestedTemplatesByPriceBand,
    priceBands,
    priceBandAbbrvLabel,
    suggestedTemplatesLabel,
    selectedTemplatesLabel,
    currency,
    intl,
  },
) => () => {
  let priceRange;
  let chartData = priceBands.map((priceBand, i) => {
    let min = formatCurrencyGeneral(priceBand.min, currency, intl);
    let max = formatCurrencyGeneral(priceBand.max, currency, intl);

    if (i < priceBands.length - 1) {
      priceRange = `${min} - ${max}`;
    } else {
      priceRange = `${min}+`;
    }

    return {
      priceBandId: priceBand.id,
      priceBandLabel: `${priceBandAbbrvLabel}${priceBand.id} ${priceRange}`,
      priceRange: priceRange,
    };
  });

  chartData.forEach(pb => {
    pb[selectedTemplatesLabel] = selectedTemplatesByPriceBand[pb.priceBandId];
    pb[suggestedTemplatesLabel] = suggestedTemplatesByPriceBand[pb.priceBandId];
  });

  return { data: chartData.sort((a, b) => a.priceBandId - b.priceBandId) };
};

export const selectedColor = ({ theme }) => theme.charts.comparison.selected;

export default compose(
  injectIntl,
  addIntlProps({
    messages,
    propNames: [
      'currency',
      'priceBandAbbrvLabel',
      'suggestedTemplatesLabel',
      'selectedTemplatesLabel',
      'clearFiltersLabel',
      'quantityAbbrvLabel',
    ],
  }),
  connect(mapStateToProps),
  withStateHandlers(initialState, {
    toggleSelectedLine,
    toggleSuggestedArea,
    resetFilters,
  }),
  addProps({ selectedFilterStyle, suggestedFilterStyle, selectedColor }),
  withStateHandlers({ data: [] }, { formatChartData }),
  lifecycle({ componentDidMount, componentDidUpdate }),
);
